// SPDX-FileCopyrightText: Copyright (c) 2025-2026 provide.io llc. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { getConfig } from './config';
import { validateOtlpEndpoint } from './endpoint';
import { wrapResilientExporter } from './resilient-exporter';
/** Pino level number → OTel SeverityNumber (from @opentelemetry/api-logs). */
const SEVERITY_MAP = {
    10: 1, // TRACE
    20: 5, // DEBUG
    30: 9, // INFO
    40: 13, // WARN
    50: 17, // ERROR
    60: 21, // FATAL
};
const SEVERITY_TEXT = {
    10: 'TRACE',
    20: 'DEBUG',
    30: 'INFO',
    40: 'WARN',
    50: 'ERROR',
    60: 'FATAL',
};
const DEFAULT_SEVERITY = 9; // INFO
/** Internal singleton — set by setupOtelLogProvider, read by emitLogRecord. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _loggerProvider = null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _otelLogger = null;
function normalizeEndpoint(endpoint) {
    const trimmed = endpoint?.trim();
    return trimmed ? trimmed : undefined;
}
function appendSignalPath(endpoint, signalPath) {
    return `${endpoint.replace(/\/+$/, '')}${signalPath}`;
}
/**
 * Construct an OTLPLogExporter + LoggerProvider and register it globally.
 * Returns a ShutdownableProvider so the caller can flush/shutdown it.
 * Throws if any peer dep is missing (caught by the caller in otel.ts).
 */
export async function setupOtelLogProvider(cfg) {
    const headers = cfg.otlpHeaders ?? {};
    const endpoint = normalizeEndpoint(cfg.otlpLogsEndpoint) ?? normalizeEndpoint(cfg.otlpEndpoint);
    if (!endpoint) {
        throw new Error('setupOtelLogProvider called without an OTLP log endpoint configured');
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sdkLogs = await import('@opentelemetry/sdk-logs');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const otlpLogs = await import('@opentelemetry/exporter-logs-otlp-http');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const apiLogs = await import('@opentelemetry/api-logs');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const res = await import('@opentelemetry/resources');
    const { LoggerProvider, BatchLogRecordProcessor } = sdkLogs;
    const { OTLPLogExporter } = otlpLogs;
    const { logs } = apiLogs;
    const { resourceFromAttributes } = res;
    const logsEndpoint = normalizeEndpoint(cfg.otlpLogsEndpoint) ?? appendSignalPath(endpoint, '/v1/logs');
    validateOtlpEndpoint(logsEndpoint);
    const logsHeaders = cfg.otlpLogsHeaders ?? headers;
    const rawLogExporter = new OTLPLogExporter({
        url: logsEndpoint,
        headers: logsHeaders,
        // Fall back to 10s when the caller supplies a TelemetryConfig without the
        // field set (e.g. tests constructing partial configs). Production callers
        // always receive the DEFAULTS-merged config from setupTelemetry().
        timeoutMillis: cfg.exporterLogsTimeoutMs ?? 10000,
    });
    // Wrap so every batch export applies retry/timeout/circuit-breaker policy.
    const logExporter = wrapResilientExporter('logs', rawLogExporter);
    const processor = new BatchLogRecordProcessor(logExporter);
    const provider = new LoggerProvider({
        resource: resourceFromAttributes({
            'service.name': cfg.serviceName,
            'deployment.environment': cfg.environment,
            'service.version': cfg.version,
        }),
        processors: [processor],
    });
    logs.setGlobalLoggerProvider(provider);
    _loggerProvider = provider;
    _otelLogger = logs.getLogger('@provide-io/telemetry');
    return provider;
}
/**
 * Emit a pino log record to the OTel LoggerProvider.
 * Called from makeWriteHook() on every log line after enrichment and sanitization.
 * No-op when no provider is registered (graceful degradation).
 */
export function emitLogRecord(o) {
    if (!_otelLogger)
        return;
    const level = o['level'] ?? 30;
    const body = String(o['message'] ?? o['event'] ?? '');
    const severityNumber = SEVERITY_MAP[level] ?? DEFAULT_SEVERITY;
    const severityText = SEVERITY_TEXT[level] ?? 'INFO';
    // Build attributes: everything except the pino-internal fields already
    // represented by body / severity / timestamp.
    const SKIP = new Set(['message', 'level', 'time', 'v']);
    const attributes = {};
    for (const [k, v] of Object.entries(o)) {
        if (!SKIP.has(k) && v !== undefined)
            attributes[k] = v;
    }
    // — Security: truncate long attribute values —
    const cfg = getConfig();
    const maxLen = cfg.securityMaxAttrValueLength;
    for (const [k, v] of Object.entries(attributes)) {
        if (typeof v === 'string' && v.length > maxLen) {
            attributes[k] = v.slice(0, maxLen) + '...';
        }
    }
    // — Security: limit attribute count —
    const maxCount = cfg.securityMaxAttrCount;
    const keys = Object.keys(attributes);
    if (keys.length > maxCount) {
        for (const k of keys.slice(maxCount)) {
            delete attributes[k];
        }
    }
    // — Code attributes: map provide-telemetry fields to OTel semantic conventions —
    if (cfg.logCodeAttributes) {
        if (attributes['caller_file']) {
            attributes['code.filepath'] = attributes['caller_file'];
        }
        if (attributes['caller_line']) {
            attributes['code.lineno'] = attributes['caller_line'];
        }
        if (attributes['name']) {
            attributes['code.namespace'] = attributes['name'];
        }
    }
    _otelLogger.emit({
        body,
        severityNumber,
        severityText,
        attributes,
        timestamp: typeof o['time'] === 'number' ? o['time'] : Date.now(),
    });
}
/** Exposed for tests and resetTelemetryState(). */
export function _resetOtelLogProviderForTests() {
    _loggerProvider = null;
    _otelLogger = null;
}
/** Exposed for integration tests to inspect state. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function _getOtelLogProvider() {
    return _loggerProvider;
}
